import dayjs from "dayjs";
import React, { useEffect, useRef } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Typography } from "@/Components/v2/atoms/Typography";

interface ActivityItem {
  id: string;
  createdAt: string;
  content: React.ReactNode;
}

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  nextCursor: string | null;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  nextCursor,
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);

  // Group activities by date using a Map
  const groupedActivities = new Map<string, ActivityItem[]>();
  activities.forEach((activity) => {
    const date = dayjs(activity.createdAt).format("Do MMMM YYYY");
    if (!groupedActivities.has(date)) {
      groupedActivities.set(date, []);
    }
    groupedActivities.get(date)!.push(activity);
  });

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries?.[0]?.isIntersecting && hasNextPage && nextCursor !== null) {
        fetchNextPage();
      }
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [fetchNextPage, hasNextPage, nextCursor]);

  if (isLoading && !activities.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <CircularProgress size={100} />
      </Box>
    );
  }

  return (
    <Box
      ref={parentRef}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {Array.from(groupedActivities.entries()).map(([date, dateActivities]) => (
        <div key={date}>
          <Box px={2} py={3}>
            <Typography variant="body2" fontWeight={600}>
              {date}
            </Typography>
            {dateActivities.map((activity) => (
              <React.Fragment key={activity.id}>{activity.content}</React.Fragment>
            ))}
          </Box>
          <Divider />
        </div>
      ))}
      {isFetchingNextPage && <CircularProgress size={50} />}
      <div ref={loadMoreRef} style={{ height: "1px" }} />
    </Box>
  );
};

export default ActivityFeed;
